// src/containers/footer/index.js
import React from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import { faMapMarkerAlt, faEnvelopeOpen, faPhoneVolume } from '@fortawesome/free-solid-svg-icons';
import { ArrowRightCircle, Facebook, Twitter, Linkedin } from 'react-feather';
import { Title, Description } from '../../components/common/title/index';
import Icon from '../../components/common/icon/index';
import Image from '../../components/common/image/index';
import './footer.scss';

const logo = '/assets/images/Logo.png'; // served from CRA's public/ folder

// Same anchors the Menu uses, so the footer stays in step with PublicHome.
const SECTION_LINKS = [
  { label: 'Home', href: '#home' },
  { label: 'Why SBL', href: '#service' },
  { label: 'Learn More', href: '#preview' },
  { label: 'Products', href: '#products' },
  { label: 'Sign Up', href: '#signup' },
  { label: 'Contact', href: '#contact' },
];

const PAGE_LINKS = [
  { label: 'Terms & Conditions', to: '/terms-of-service' },
  { label: 'Privacy & Policy', to: '/privacy-policy' },
];

// Fill these in when the accounts are live. '#' keeps the markup valid
// without an empty href, which would reload the page on click.
const SOCIAL_LINKS = [
  { label: 'Facebook', href: '#', Glyph: Facebook },
  { label: 'Twitter', href: '#', Glyph: Twitter },
  { label: 'LinkedIn', href: '#', Glyph: Linkedin },
];

const ABOUT_TEXT =
  'Smart Board Learning LLC creates safe, engaging, and innovative digital learning ' +
  'experiences for children, parents, and educators. Our mission is to make education ' +
  'fun and accessible by blending interactive content, music, games, and creative tools. ' +
  "We're committed to protecting families online, using kid-friendly design and strong " +
  'privacy practices, while giving adults the insights they need to support learning.';

function Footer() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const offset = () => {
    const header = document.querySelector('.header-sticky');
    return header ? header.offsetHeight + 10 : 90;
  };

  return (
    <div className="footer-wrapper">
      <Container>
        <div className="footer-content-wrapper">
          <Row>
            <Col lg={4} sm={6}>
              <div className="footer-logo-wrapper">
                <Image Path={logo} Class="footer-logo" />
                <Title Class="footer-title" Name="About Us" />
                <Description Class="footer-dec" Name={ABOUT_TEXT} />
              </div>
            </Col>

            <Col lg={3} sm={6}>
              <div className="footer-contact-us">
                <Title Class="footer-title" Name="Contact Us" />
                <div className="footer-details-link">
                  <ul>
                    <li>
                      <span className="footer-static">
                        <span className="footer-icon"><Icon icon={faMapMarkerAlt} /></span>
                        <Title Class="footer-link" Name="P.O. Box 22, Ashburn, VA 20146 USA" />
                      </span>
                    </li>
                    <li>
                      <a href="mailto:team@smartboardlearning.com">
                        <span className="footer-icon"><Icon icon={faEnvelopeOpen} /></span>
                        <Title Class="footer-link footer-email" Name="team@smartboardlearning.com" />
                      </a>
                    </li>
                    <li>
                      <a href="tel:5715636142">
                        <span className="footer-icon"><Icon icon={faPhoneVolume} /></span>
                        <Title Class="footer-link" Name="(571) 563-6142" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </Col>

            <Col lg={2} sm={6}>
              <div className="footer-about-us">
                <div className="footer-about-outer">
                  <Title Class="footer-title" Name="Explore" />
                  <div className="footer-details-link">
                    <ul>
                      {SECTION_LINKS.map((item) => (
                        <li key={item.href}>
                          {isHomePage ? (
                            <AnchorLink offset={offset} href={item.href}>
                              <ArrowRightCircle />
                              <Title Class="footer-link footer-arrow" Name={item.label} />
                            </AnchorLink>
                          ) : (
                            <Link to={`/${item.href}`}>
                              <ArrowRightCircle />
                              <Title Class="footer-link footer-arrow" Name={item.label} />
                            </Link>
                          )}
                        </li>
                      ))}

                      {PAGE_LINKS.map((item) => (
                        <li key={item.to}>
                          <Link to={item.to}>
                            <ArrowRightCircle />
                            <Title Class="footer-link footer-arrow" Name={item.label} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={3} sm={6}>
              <div className="footer-newsletter">
                <Title Class="footer-social-title" Name="Follow Us" />
                <ul className="social-wrapper">
                  {SOCIAL_LINKS.map(({ label, href, Glyph }) => (
                    <li key={label}>
                      <a
                        href={href}
                        aria-label={label}
                        target={href === '#' ? undefined : '_blank'}
                        rel={href === '#' ? undefined : 'noreferrer'}
                      >
                        <span><Glyph /></span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
}

export default Footer;
